import { Injectable, inject, signal } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';

@Injectable({
    providedIn: 'root'
})
export class FcpMessageService {
    private messageService = inject(MessageService);
    private currentPosition = signal<MessagePosition>(MessagePosition.BottomRight);

    /**
     * Expose position as readonly signal for components to consume
     */
    position = this.currentPosition.asReadonly();

    /**
     * Displays a single message
     * @param message - The message to display
     */
    add(message: FcpMessage): void {
        if (message.position) {
            this.currentPosition.set(message.position);
        }

        const severityIconMap = new Map([
            [FcpMessageSeverity.Success, 'fc-font-icon-circle-check-fill'],
            [FcpMessageSeverity.Warning, 'fc-font-icon-circle-exclamation-fill'],
            [FcpMessageSeverity.Error, 'fc-font-icon-circle-warning-fill'],
            [FcpMessageSeverity.Info, 'fc-font-icon-circle-info-fill'],
            [FcpMessageSeverity.Secondary, 'fc-font-icon-circle-info-fill']
        ]);

        const iconClass =
            severityIconMap.get(message.severity as FcpMessageSeverity) ?? 'fc-font-icon-circle-info-fill';
        message.icon = `fc-font-icon ${iconClass}`;

        this.messageService.add(message);
    }

    /**
     * Displays multiple messages
     * @param messages - The messages to display
     */
    addAll(messages: FcpMessage[]): void {
        this.messageService.addAll(messages);
    }

    /**
     * Clears all messages or messages with specific key
     * @param key - The key of the messages to clear
     */
    clear(key?: string): void {
        this.messageService.clear(key);
    }

    /**
     * Sets the default position for future toast messages
     * @param position - The position where toasts should appear
     */
    setDefaultPosition(position: MessagePosition): void {
        this.currentPosition.set(position);
    }

    /**
     * Gets the current default position
     * @returns The current toast position
     */
    getCurrentPosition(): MessagePosition {
        return this.currentPosition();
    }
}

export enum MessagePosition {
    TopLeft = 'top-left',
    TopCenter = 'top-center',
    TopRight = 'top-right',
    BottomLeft = 'bottom-left',
    BottomCenter = 'bottom-center',
    BottomRight = 'bottom-right',
    Center = 'center'
}

export enum FcpMessageSeverity {
    Success = 'success',
    Info = 'info',
    Warning = 'warn',
    Error = 'error',
    Secondary = 'secondary'
}

export interface FcpMessage extends ToastMessageOptions {
    position?: MessagePosition;
    action?: {
        text: string;
        url: string;
    };
}
